import type { StorageThreadType } from "@mastra/core/memory";
import { DateTime } from "luxon";

export interface ThreadHistorySection {
  id: string;
  title: string;
  threads: StorageThreadType[];
}

/** Reads a thread's creation time, tolerating the Mastra SDK's loose typing. */
export function getThreadCreatedAt(
  thread: StorageThreadType,
): DateTime | undefined {
  // `createdAt` is incorrectly typed in Mastra client SDK — in reality it's a string.
  const rawCreatedAt = thread.createdAt as Date | string | undefined;

  let createdAt: DateTime | undefined;
  if (rawCreatedAt instanceof Date) {
    createdAt = DateTime.fromJSDate(rawCreatedAt);
  } else if (typeof rawCreatedAt === "string") {
    createdAt = DateTime.fromISO(rawCreatedAt);
  }

  // Normalize invalid timestamps to undefined so callers' fallbacks apply —
  // an invalid DateTime is truthy and would otherwise poison sorts (NaN) and
  // render as "Invalid DateTime".
  return createdAt?.isValid ? createdAt : undefined;
}

function getThreadHistorySectionKey(
  createdAt: DateTime | undefined,
  now: DateTime,
): { id: string; title: string } {
  if (!createdAt?.isValid) {
    return { id: "older", title: "Older" };
  }

  const localCreatedAt = createdAt.toLocal();
  const startOfToday = now.startOf("day");
  const startOfYesterday = startOfToday.minus({ days: 1 });
  const startOfWeek = now.startOf("week");
  const startOfLastWeek = startOfWeek.minus({ weeks: 1 });
  const startOfMonth = now.startOf("month");
  const startOfLastMonth = startOfMonth.minus({ months: 1 });

  if (localCreatedAt >= startOfToday) {
    return { id: "today", title: "Today" };
  }

  if (localCreatedAt >= startOfYesterday) {
    return { id: "yesterday", title: "Yesterday" };
  }

  // Earlier this week: label by weekday name ("Monday").
  if (localCreatedAt >= startOfWeek) {
    return {
      id: localCreatedAt.toFormat("cccc").toLowerCase(),
      title: localCreatedAt.toFormat("cccc"),
    };
  }

  if (localCreatedAt >= startOfLastWeek) {
    return { id: "last-week", title: "Last week" };
  }

  if (localCreatedAt >= startOfMonth) {
    return { id: "earlier-this-month", title: "Earlier this month" };
  }

  if (localCreatedAt >= startOfLastMonth) {
    return { id: "last-month", title: "Last month" };
  }

  // Older: label by month, adding the year once it isn't the current one.
  const isCurrentYear = localCreatedAt.year === now.year;

  return {
    id: localCreatedAt.toFormat("yyyy-LL"),
    title: localCreatedAt.toFormat(isCurrentYear ? "LLLL" : "LLLL yyyy"),
  };
}

/**
 * Groups threads into time-bucketed sections (Today, Yesterday, weekday names
 * for earlier this week, Last week, Earlier this month, Last month, then
 * month labels), newest section and newest thread first.
 */
export function buildThreadHistorySections(
  threads: readonly StorageThreadType[],
  now: DateTime = DateTime.now(),
): ThreadHistorySection[] {
  // Sections need no ordering pass of their own: the buckets run oldest to
  // newest in time, so walking the threads newest-first creates them in
  // display order and the map preserves it.
  const sectionsById = new Map<string, ThreadHistorySection>();

  const sortedThreads = [...threads].sort((firstThread, secondThread) => {
    const firstCreatedAt = getThreadCreatedAt(firstThread)?.toMillis() ?? 0;
    const secondCreatedAt = getThreadCreatedAt(secondThread)?.toMillis() ?? 0;
    return secondCreatedAt - firstCreatedAt;
  });

  for (const thread of sortedThreads) {
    const sectionKey = getThreadHistorySectionKey(
      getThreadCreatedAt(thread),
      now,
    );
    const existingSection = sectionsById.get(sectionKey.id);

    if (existingSection) {
      existingSection.threads.push(thread);
      continue;
    }

    sectionsById.set(sectionKey.id, {
      id: sectionKey.id,
      title: sectionKey.title,
      threads: [thread],
    });
  }

  return [...sectionsById.values()];
}
